import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { unifiedApiService } from '../../../services/unified';
import { useEventTracking } from '../../../hooks/useAnalytics';
import { VideoUpload } from '../../../components/applications/VideoUpload';
import type { Job } from '../../../types/job';
import type { Resume } from '../../../types/resume';

interface JobApplicationModalProps {
  job: Job;
  onClose: () => void;
  onSuccess: () => void;
}



export function JobApplicationModal({ job, onClose, onSuccess }: JobApplicationModalProps) {
  useAuth(); // Ensure user is authenticated
  const { trackJobApplication } = useEventTracking();
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState<string>('');
  const [coverLetter, setCoverLetter] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'select-resume' | 'application-form' | 'confirmation'>('select-resume');
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [uploadedVideoId, setUploadedVideoId] = useState<string | null>(null);
  const [videoUploadError, setVideoUploadError] = useState<string | null>(null);
  const [showVideoUpload, setShowVideoUpload] = useState(false);

  useEffect(() => {
    // Load user's resumes
    const loadResumes = async () => {
      try {
        const userResumes = await unifiedApiService.resume.getUserResumes();
        setResumes(userResumes as any);
        
        // Auto-select the highest scoring resume
        const bestResume = userResumes
          .filter(r => r.status === 'scanned' && r.score)
          .sort((a, b) => (b.score || 0) - (a.score || 0))[0];
        
        if (bestResume) {
          setSelectedResumeId(bestResume.id);
        }
      } catch (err) {
        const error = err as Error;
        setError(error.message || 'Failed to load resumes');
      }
    };

    loadResumes();
  }, []);

  const handleResumeSelect = () => {
    if (!selectedResumeId) {
      setError('Please select a resume to continue');
      return;
    }
    setError(null);
    setStep('application-form');
  };

  const handleSubmitApplication = async () => {
    if (!selectedResumeId) {
      setError('Please select a resume');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // TODO: Replace with actual API call
      // const applicationData: CreateJobApplicationRequest = {
      //   jobId: job.id,
      //   resumeId: selectedResumeId,
      //   coverLetter: coverLetter.trim() || undefined,
      //   additionalInfo: additionalInfo.trim() || undefined
      // };
      // const application = await JobService.applyForJob(applicationData);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock application ID - in real implementation, this comes from the API response
      const mockAppId = 'app_' + Date.now();
      setApplicationId(mockAppId);
      
      // If user wants to upload video, stay on form to allow upload
      // Otherwise go to confirmation
      if (showVideoUpload && !uploadedVideoId) {
        // Stay on application-form step to allow video upload
        setIsSubmitting(false);
      } else {
        setStep('confirmation');
        
        // Track job application event
        trackJobApplication(job.id.toString(), job.title);
        
        // Auto-close after showing confirmation
        setTimeout(() => {
          onSuccess();
        }, 3000);
      }
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to submit application. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleVideoUploadComplete = (videoId: string) => {
    setUploadedVideoId(videoId);
    setVideoUploadError(null);
    
    // After video upload, move to confirmation
    setStep('confirmation');
    
    // Track job application event
    trackJobApplication(job.id.toString(), job.title);
    
    // Auto-close after showing confirmation
    setTimeout(() => {
      onSuccess();
    }, 3000);
  };

  const handleVideoUploadError = (errorMessage: string) => {
    setVideoUploadError(errorMessage);
  };

  const handleSkipVideo = () => {
    // User chose to skip video upload
    setStep('confirmation');
    
    // Track job application event
    trackJobApplication(job.id.toString(), job.title);
    
    // Auto-close after showing confirmation
    setTimeout(() => {
      onSuccess();
    }, 3000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: Resume['status']) => {
    switch (status) {
      case 'scanned':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getScoreColor = (score?: number) => {
    if (!score) return 'text-gray-500';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {step === 'select-resume' && 'Select Resume'}
                {step === 'application-form' && 'Complete Application'}
                {step === 'confirmation' && 'Application Submitted'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Applying for: <span className="font-medium">{job.title}</span> at {job.company}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center mt-4 space-x-4">
            <div className={`flex items-center ${step !== 'select-resume' ? 'text-primary-600' : 'text-gray-900'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step !== 'select-resume' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700'
              }`}>
                {step !== 'select-resume' ? '✓' : '1'}
              </div>
              <span className="ml-2 text-sm font-medium">Select Resume</span>
            </div>
            <div className={`w-8 h-0.5 ${step === 'confirmation' ? 'bg-primary-600' : 'bg-gray-300'}`}></div>
            <div className={`flex items-center ${step === 'confirmation' ? 'text-primary-600' : step === 'application-form' ? 'text-gray-900' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'confirmation' ? 'bg-primary-600 text-white' : step === 'application-form' ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-400'
              }`}>
                {step === 'confirmation' ? '✓' : '2'}
              </div>
              <span className="ml-2 text-sm font-medium">Application</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Step 1: Resume Selection */}
          {step === 'select-resume' && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Your Resume</h3>
                <p className="text-gray-600 text-sm">
                  Select the resume you'd like to submit with your application. We recommend using your highest-scoring resume.
                </p>
              </div>

              {resumes.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Resumes Found</h3>
                  <p className="text-gray-600 mb-4">
                    You need to upload a resume before applying for jobs.
                  </p>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                  >
                    Upload Resume
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {resumes.map((resume) => (
                    <div
                      key={resume.id}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                        selectedResumeId === resume.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedResumeId(resume.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            checked={selectedResumeId === resume.id}
                            onChange={() => setSelectedResumeId(resume.id)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                          />
                          <div>
                            <h4 className="font-medium text-gray-900">{resume.originalFilename || resume.filename}</h4>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(resume.status)}`}>
                                {resume.status.toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-500">
                                Uploaded {formatDate(resume.submittedAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                        {resume.score && (
                          <div className="text-right">
                            <div className={`text-lg font-bold ${getScoreColor(resume.score)}`}>
                              {resume.score}%
                            </div>
                            <div className="text-xs text-gray-500">Match Score</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Application Form */}
          {step === 'application-form' && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Your Application</h3>
                <p className="text-gray-600 text-sm">
                  Add a cover letter and any additional information to strengthen your application.
                </p>
              </div>

              <div className="space-y-6">
                {/* Selected Resume Display */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Selected Resume</h4>
                  {(() => {
                    const selectedResume = resumes.find(r => r.id === selectedResumeId);
                    return selectedResume ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium text-gray-700">{selectedResume.originalFilename || selectedResume.filename}</span>
                          {selectedResume.score && (
                            <span className={`ml-2 text-sm font-bold ${getScoreColor(selectedResume.score)}`}>
                              ({selectedResume.score}% match)
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => setStep('select-resume')}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          Change
                        </button>
                      </div>
                    ) : null;
                  })()}
                </div>

                {/* Cover Letter */}
                <div>
                  <label htmlFor="coverLetter" className="block text-sm font-medium text-gray-900 mb-2">
                    Cover Letter <span className="text-gray-500">(Optional)</span>
                  </label>
                  <textarea
                    id="coverLetter"
                    value={coverLetter}
                    onChange={(e) => setCoverLetter(e.target.value)}
                    rows={6}
                    placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {coverLetter.length}/1000 characters
                  </p>
                </div>

                {/* Additional Information */}
                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-900 mb-2">
                    Additional Information <span className="text-gray-500">(Optional)</span>
                  </label>
                  <textarea
                    id="additionalInfo"
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    rows={4}
                    placeholder="Any additional information you'd like to share (portfolio links, availability, etc.)..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  />
                </div>

                {/* Demo Video Upload */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-900">
                      Demo Video <span className="text-gray-500">(Optional)</span>
                    </label>
                    {!showVideoUpload && !uploadedVideoId && !applicationId && (
                      <button
                        type="button"
                        onClick={() => setShowVideoUpload(true)}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        + Add Video
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Upload a demo video (5-15 minutes) to showcase your skills and personality
                  </p>
                  
                  {/* Show video upload if application has been created */}
                  {showVideoUpload && !uploadedVideoId && applicationId && (
                    <div>
                      <VideoUpload
                        applicationId={applicationId}
                        onUploadComplete={handleVideoUploadComplete}
                        onUploadError={handleVideoUploadError}
                      />
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={handleSkipVideo}
                          className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                        >
                          Skip video and continue →
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Show info message if user wants to add video but application not yet created */}
                  {showVideoUpload && !applicationId && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex">
                        <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-blue-800">
                          Click "Submit Application" below to create your application, then you'll be able to upload your demo video.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Show success message if video uploaded */}
                  {uploadedVideoId && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm font-medium text-green-800">Demo video uploaded successfully</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show error if video upload failed */}
                  {videoUploadError && !uploadedVideoId && applicationId && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 text-sm">{videoUploadError}</p>
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 'confirmation' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Application Submitted Successfully!</h3>
              <p className="text-gray-600 mb-4">
                Your application for <span className="font-medium">{job.title}</span> at {job.company} has been submitted.
              </p>
              {applicationId && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600">
                    Application ID: <span className="font-mono font-medium">{applicationId}</span>
                  </p>
                </div>
              )}
              <div className="space-y-2 text-sm text-gray-600">
                <p>✓ You will receive an email confirmation shortly</p>
                <p>✓ The hiring team will review your application</p>
                {uploadedVideoId && <p>✓ Your demo video has been included with your application</p>}
                <p>✓ You can track your application status in your dashboard</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 'confirmation' && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between">
            <button
              onClick={step === 'application-form' && !applicationId ? () => setStep('select-resume') : onClose}
              disabled={!!(applicationId && showVideoUpload && !uploadedVideoId)}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {step === 'application-form' && !applicationId ? 'Back' : 'Cancel'}
            </button>
            
            {/* Show submit button only if application hasn't been created yet */}
            {!applicationId && (
              <button
                onClick={step === 'select-resume' ? handleResumeSelect : handleSubmitApplication}
                disabled={isSubmitting || (step === 'select-resume' && !selectedResumeId)}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
              >
                {isSubmitting && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {step === 'select-resume' ? 'Continue' : isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}